import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Button,
  FieldLabel,
  SurfaceAutocomplete,
  SurfaceTextField,
} from '../../../components/design-system';

export type SiteDeleteAction = 'delete-all' | 'delete-site-only';

export interface SiteDeleteDestination {
  id: string;
  name: string;
}

export interface SiteDeleteRequest {
  deleteFinds: boolean;
  moveObjects: boolean;
  newSiteName?: string;
  targetSiteId?: string;
}

interface SiteDeleteDialogProps {
  action: SiteDeleteAction;
  destinations: SiteDeleteDestination[];
  error?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (request: SiteDeleteRequest) => void;
  open: boolean;
}

const NONE_DESTINATION = '__none__';
const CREATE_DESTINATION = '__create__';
const contentStyles = { pt: 1.5 };
const contentStackStyles = { gap: 2.5, minWidth: { sm: 420 } };
const fieldStyles = { gap: 0.75 };
const objectLabelStyles = { alignItems: 'flex-start', m: 0 };
const objectCheckboxStyles = { p: 0, mr: 1, mt: '1px' };
const objectMainLabelStyles = { lineHeight: '20px' };
const objectHelpStyles = { color: 'text.secondary', mt: 0.25 };
const warningStyles = { mt: 0.5 };
const destinationOptionStyles = {
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
};
const createIconStyles = {
  alignItems: 'center',
  bgcolor: 'success.main',
  borderRadius: '50%',
  color: 'success.contrastText',
  display: 'inline-flex',
  flexShrink: 0,
  height: 20,
  justifyContent: 'center',
  width: 20,
};
const createIconSvgStyles = { fontSize: 16 };

type SiteDeleteOptionKind = 'none' | 'create' | 'site';

interface SiteDeleteOption {
  id: string;
  kind: SiteDeleteOptionKind;
  name: string;
}

const filterSiteOptions = createFilterOptions<SiteDeleteOption>({
  stringify: (option) => option.name,
});

const filterDestinationOptions = (
  options: SiteDeleteOption[],
  state: FilterOptionsState<SiteDeleteOption>
) => {
  const fixedOptions = options.filter((option) => option.kind !== 'site');
  const siteOptions = options.filter((option) => option.kind === 'site');

  return [...fixedOptions, ...filterSiteOptions(siteOptions, state)];
};

const getDestinationOptionLabel = (option: SiteDeleteOption) => option.name;
const compareDestinationOptions = (
  option: SiteDeleteOption,
  value: SiteDeleteOption
) => option.id === value.id;
const renderDestinationOption = (
  optionProps: React.HTMLAttributes<HTMLLIElement>,
  option: SiteDeleteOption
) => (
  <li {...optionProps}>
    <Stack component="span" direction="row" sx={destinationOptionStyles}>
      {option.kind === 'create' && (
        <Box component="span" sx={createIconStyles}>
          <AddIcon sx={createIconSvgStyles} />
        </Box>
      )}
      <span>{option.name}</span>
    </Stack>
  </li>
);

const SiteDeleteDialog = React.memo(
  ({
    action,
    destinations,
    error,
    loading = false,
    onClose,
    onConfirm,
    open,
  }: SiteDeleteDialogProps) => {
    const intl = useIntl();
    const [destination, setDestination] = useState(NONE_DESTINATION);
    const [moveObjects, setMoveObjects] = useState(true);
    const [newSiteName, setNewSiteName] = useState('');
    const deletesFinds = action === 'delete-all';

    useEffect(() => {
      if (open) {
        setDestination(NONE_DESTINATION);
        setMoveObjects(true);
        setNewSiteName('');
      }
    }, [action, open]);

    const handleDestinationChange = useCallback(
      (_event: React.SyntheticEvent, option: SiteDeleteOption | null) => {
        setDestination(option?.id || NONE_DESTINATION);
      },
      []
    );
    const handleNewSiteNameChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewSiteName(event.target.value);
      },
      []
    );
    const handleMoveObjectsChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setMoveObjects(event.target.checked);
      },
      []
    );
    const handleConfirm = useCallback(() => {
      if (deletesFinds) {
        onConfirm({ deleteFinds: true, moveObjects: false });

        return;
      }

      onConfirm({
        deleteFinds: false,
        moveObjects,
        ...(destination === CREATE_DESTINATION
          ? { newSiteName: newSiteName.trim() }
          : {}),
        ...(destination !== NONE_DESTINATION &&
        destination !== CREATE_DESTINATION
          ? { targetSiteId: destination }
          : {}),
      });
    }, [deletesFinds, destination, moveObjects, newSiteName, onConfirm]);

    const needsSiteName = destination === CREATE_DESTINATION;
    const confirmDisabled =
      loading || (needsSiteName && newSiteName.trim().length === 0);
    const destinationLabel = intl.formatMessage({
      id: 'site.delete.destination',
      defaultMessage: 'Move finds to',
    });
    const destinationOptions = useMemo<SiteDeleteOption[]>(
      () => [
        {
          id: NONE_DESTINATION,
          kind: 'none',
          name: intl.formatMessage({
            id: 'general.none',
            defaultMessage: 'None',
          }),
        },
        {
          id: CREATE_DESTINATION,
          kind: 'create',
          name: intl.formatMessage({
            id: 'site.delete.createSite',
            defaultMessage: 'Create new site',
          }),
        },
        ...destinations.map(({ id, name }) => ({
          id,
          kind: 'site' as const,
          name,
        })),
      ],
      [destinations, intl]
    );
    const selectedDestination = useMemo(
      () =>
        destinationOptions.find(({ id }) => id === destination) ||
        destinationOptions[0],
      [destination, destinationOptions]
    );

    return (
      <Dialog open={open} onClose={loading ? undefined : onClose}>
        <DialogTitle>
          {deletesFinds ? (
            <FormattedMessage
              id="site.delete.allTitle"
              defaultMessage="Delete site and finds?"
            />
          ) : (
            <FormattedMessage
              id="site.delete.siteOnlyTitle"
              defaultMessage="Delete site only?"
            />
          )}
        </DialogTitle>
        <DialogContent sx={contentStyles}>
          <Stack sx={contentStackStyles}>
            {deletesFinds ? (
              <Alert severity="warning" sx={warningStyles}>
                <FormattedMessage
                  id="site.delete.allWarning"
                  defaultMessage="All site finds, site objects, and any other artifacts such as images will be permanently deleted along with the site. This action cannot be undone."
                />
              </Alert>
            ) : (
              <>
                <DialogContentText>
                  <FormattedMessage
                    id="site.delete.siteOnlyDescription"
                    defaultMessage="Choose where the site's finds should be moved before the site is deleted."
                  />
                </DialogContentText>
                <Stack sx={fieldStyles}>
                  <FieldLabel htmlFor="site-delete-destination">
                    {destinationLabel}
                  </FieldLabel>
                  <SurfaceAutocomplete
                    autoHighlight
                    disableClearable
                    disabled={loading}
                    filterOptions={filterDestinationOptions}
                    fullWidth
                    getOptionLabel={getDestinationOptionLabel}
                    id="site-delete-destination"
                    isOptionEqualToValue={compareDestinationOptions}
                    onChange={handleDestinationChange}
                    openOnFocus
                    options={destinationOptions}
                    renderOption={renderDestinationOption}
                    surface="sunken"
                    value={selectedDestination}
                  />
                </Stack>
                {needsSiteName && (
                  <Stack sx={fieldStyles}>
                    <FieldLabel htmlFor="site-delete-new-name">
                      <FormattedMessage
                        id="site.delete.newSiteName"
                        defaultMessage="New site name"
                      />
                    </FieldLabel>
                    <SurfaceTextField
                      autoFocus
                      disabled={loading}
                      fullWidth
                      id="site-delete-new-name"
                      onChange={handleNewSiteNameChange}
                      size="small"
                      surface="sunken"
                      value={newSiteName}
                    />
                  </Stack>
                )}
                <FormControlLabel
                  sx={objectLabelStyles}
                  control={
                    <Checkbox
                      checked={moveObjects}
                      disabled={loading}
                      onChange={handleMoveObjectsChange}
                      size="small"
                      sx={objectCheckboxStyles}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={objectMainLabelStyles}>
                        <FormattedMessage
                          id="site.delete.moveObjects"
                          defaultMessage="Perform same action for site objects"
                        />
                      </Typography>
                      <Typography variant="caption" sx={objectHelpStyles}>
                        <FormattedMessage
                          id="site.delete.moveObjectsHelp"
                          defaultMessage="If not checked, the objects will be deleted along with the site."
                        />
                      </Typography>
                    </Box>
                  }
                />
              </>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={onClose} tone="neutral">
            <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
          </Button>
          <Button disabled={confirmDisabled} onClick={handleConfirm}>
            <FormattedMessage
              id="site.delete.perform"
              defaultMessage="Perform delete"
            />
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

SiteDeleteDialog.displayName = 'SiteDeleteDialog';

export { SiteDeleteDialog };
